'use strict';

import { scrollBehavior } from './lib/motion';

export function initTabs(tabsSelector, panelsSelector, { containerId, showAllClass, initial = 'last' } = {}) {
  const tabs = Array.from(document.querySelectorAll(tabsSelector));
  const panels = Array.from(document.querySelectorAll(panelsSelector));

  // ARIA tabs pattern: the widget only gets role/aria-selected/tabindex
  // wiring, never visual markup, so every consumer of initTabs picks it up
  // without template changes.
  const tablist = tabs[0]?.parentElement;
  if (tablist) tablist.setAttribute('role', 'tablist');
  tabs.forEach(btn => {
    btn.id = btn.id || `tab-btn-${btn.dataset.tab}`;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('tabindex', '-1');
  });
  panels.forEach(panel => {
    const btn = tabs.find(t => t.dataset.tab === panel.id);
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('tabindex', '0');
    if (btn) panel.setAttribute('aria-labelledby', btn.id);
  });

  function showTab(tabId) {
    panels.forEach(p => p.classList.remove('is-active'));
    tabs.forEach(t => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });
    const panel = document.getElementById(tabId);
    const btn = tabs.find(t => t.dataset.tab === tabId);
    if (panel) panel.classList.add('is-active');
    if (btn) {
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      btn.setAttribute('tabindex', '0');
    }
  }

  function handleHashLink() {
    const hash = window.location.hash;
    const talkMatch = hash.match(/^#(talk-\d+)$/);
    const memberMatch = hash.match(/^#(member-\d+)$/);

    if (talkMatch) {
      const el = document.getElementById(talkMatch[1]);
      const panel = el?.closest('.publi-panel');
      if (panel?.id) {
        showTab(panel.id);
        setTimeout(() => el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }), 100);
      }
    } else if (memberMatch) {
      const el = document.getElementById(memberMatch[1]);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }), 50);
    }
  }

  if (tabs.length > 0) {
    showTab(tabs[initial === 'first' ? 0 : tabs.length - 1].dataset.tab);
    setTimeout(handleHashLink, 50);
  }

  tabs.forEach((btn, i) => {
    btn.addEventListener('click', () => showTab(btn.dataset.tab));

    // Standard ARIA tabs keyboard pattern: arrow keys move and activate,
    // Home/End jump to the ends.
    btn.addEventListener('keydown', e => {
      let target = null;
      if (e.key === 'ArrowRight') target = tabs[(i + 1) % tabs.length];
      else if (e.key === 'ArrowLeft') target = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === 'Home') target = tabs[0];
      else if (e.key === 'End') target = tabs[tabs.length - 1];
      if (target) {
        e.preventDefault();
        target.focus();
        showTab(target.dataset.tab);
      }
    });
  });

  // Listing-mode switch (views/partials/listing_pref.mustache). The class goes
  // on the container, not on <html>: the preference is per page view and is
  // deliberately not persisted, so the server-rendered aria-pressed="false" is
  // already correct at first paint and there is nothing to reconcile.
  const container = containerId && document.getElementById(containerId);
  const toggle = container && container.querySelector('.listing-pref__toggle');
  if (container && toggle) {
    toggle.addEventListener('click', () => {
      const on = container.classList.toggle(showAllClass);
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');

      // Collapsing back to one year removes most of the document from under
      // the reader; the browser clamps them into whatever is left, which on a
      // long list is the footer. Put them back at the top of the listing.
      // Expanding needs no equivalent — it only ever adds content below.
      //
      // Document-absolute top, not offsetTop: the container's offset parent is
      // #main, not the page. Reading it after the toggle is correct — nothing
      // above the container moved.
      if (!on) {
        const top = container.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY > top) {
          container.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        }
      }
    });
  }
}
